import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { fetchProductById } from "../../../slice/product/productSlice";

const API = process.env.REACT_APP_API_URL;
const ORIENTATIONS = ["landscape", "portrait"];

/* ── Zone overlay rendered on top of preview image ── */
function ZoneOverlay({ zones = [], color, label }) {
  if (!zones.length) return null;
  return zones.map((zone, idx) => {
    const hasQuad = !!zone.quad;
    const style = hasQuad
      ? (() => {
          const { TL, TR, BR, BL } = zone.quad;
          const minX = Math.min(TL.x, TR.x, BR.x, BL.x);
          const minY = Math.min(TL.y, TR.y, BR.y, BL.y);
          const maxX = Math.max(TL.x, TR.x, BR.x, BL.x);
          const maxY = Math.max(TL.y, TR.y, BR.y, BL.y);
          const rx   = (p) => `${((p - minX) / (maxX - minX)) * 100}%`;
          const ry   = (p) => `${((p - minY) / (maxY - minY)) * 100}%`;
          return {
            position: "absolute",
            left:     `${minX}%`, top:    `${minY}%`,
            width:    `${maxX - minX}%`, height: `${maxY - minY}%`,
            clipPath: `polygon(${rx(TL.x)} ${ry(TL.y)}, ${rx(TR.x)} ${ry(TR.y)}, ${rx(BR.x)} ${ry(BR.y)}, ${rx(BL.x)} ${ry(BL.y)})`,
          };
        })()
      : {
          position: "absolute",
          left:   `${zone.x}%`, top:    `${zone.y}%`,
          width:  `${zone.width}%`,  height: `${zone.height}%`,
        };

    return (
      <div
        key={idx}
        style={{
          ...style,
          background:   `${color}22`,
          border:       `2px solid ${color}`,
          borderRadius: "4px",
          display:      "flex",
          alignItems:   "center",
          justifyContent: "center",
        }}
      >
        <span style={{
          background: color, color: "#fff",
          borderRadius: "12px", padding: "2px 8px",
          fontSize: "10px", fontWeight: 700, whiteSpace: "nowrap",
        }}>
          {label} {idx + 1}
        </span>
      </div>
    );
  });
}

/* ── One orientation panel (landscape or portrait) ── */
function OrientationView({ oriData, orientation }) {
  const [selectedImg, setSelectedImg] = useState(0);
  const images      = oriData?.images      || [];
  const previewImage = oriData?.previewImage
    ? `${API}/uploads/${oriData.previewImage}`
    : null;
  const photoZones  = oriData?.photoZones  || [];
  const textZones   = oriData?.textZones   || [];
  const icon        = orientation === "landscape" ? "🖼️" : "📱";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

      {/* ── Product images ── */}
      <div>
        <p style={{ fontWeight: 600, fontSize: "13px", color: "#374151", marginBottom: "10px" }}>
          {icon} {orientation.charAt(0).toUpperCase() + orientation.slice(1)} — Product Images
        </p>

        {images.length > 0 ? (
          <>
            {/* Main image */}
            <div style={{
              height: orientation === "landscape" ? "280px" : "360px",
              background: "#f8f9fa", borderRadius: "10px",
              border: "1.5px solid #e5e7eb", overflow: "hidden",
              display: "flex", alignItems: "center", justifyContent: "center",
              marginBottom: "10px",
            }}>
              <img
                src={`${API}/uploads/${images[selectedImg]}`}
                alt={`product-${selectedImg}`}
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
              />
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {images.map((img, idx) => (
                  <div
                    key={idx}
                    onClick={() => setSelectedImg(idx)}
                    style={{
                      width: "64px", height: "64px", borderRadius: "8px",
                      overflow: "hidden", cursor: "pointer",
                      border: `2px solid ${selectedImg === idx ? "#0d6efd" : "#e5e7eb"}`,
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={`${API}/uploads/${img}`}
                      alt={`thumb-${idx}`}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div style={{
            height: "160px", background: "#f8f9fa", borderRadius: "10px",
            border: "2px dashed #d1d5db", display: "flex",
            alignItems: "center", justifyContent: "center",
            flexDirection: "column", color: "#9ca3af",
          }}>
            <span style={{ fontSize: "32px" }}>🖼️</span>
            <span style={{ fontSize: "13px", marginTop: "6px" }}>No images uploaded</span>
          </div>
        )}
      </div>

      {/* ── Preview image + zones ── */}
      {previewImage && (
        <div>
          <p style={{ fontWeight: 600, fontSize: "13px", color: "#374151", marginBottom: "10px" }}>
            {icon} {orientation.charAt(0).toUpperCase() + orientation.slice(1)} — Preview &amp; Zones
          </p>

          <div style={{ position: "relative", display: "inline-block", width: "100%", lineHeight: 0 }}>
            <img
              src={previewImage}
              alt="Preview"
              style={{ width: "100%", borderRadius: "10px", display: "block", border: "1.5px solid #e5e7eb" }}
            />
            <ZoneOverlay zones={photoZones} color="#F59E0B" label="Photo" />
            <ZoneOverlay zones={textZones}  color="#6366F1" label="Text"  />
          </div>

          {/* Zone legend */}
          {(photoZones.length > 0 || textZones.length > 0) && (
            <div style={{ display: "flex", gap: "12px", marginTop: "10px", flexWrap: "wrap" }}>
              {photoZones.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px" }}>
                  <span style={{ width: "12px", height: "12px", background: "#F59E0B", borderRadius: "3px", flexShrink: 0 }} />
                  <span style={{ color: "#6b7280" }}>{photoZones.length} Photo Zone{photoZones.length > 1 ? "s" : ""}</span>
                </div>
              )}
              {textZones.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px" }}>
                  <span style={{ width: "12px", height: "12px", background: "#6366F1", borderRadius: "3px", flexShrink: 0 }} />
                  <span style={{ color: "#6b7280" }}>{textZones.length} Text Zone{textZones.length > 1 ? "s" : ""}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Per-color variant card ── */
function ColorVariantCard({ variant, colors }) {
  const [activeOri, setActiveOri] = useState("landscape");
  const colorObj = colors.find((c) => String(c._id) === String(variant.colorId?._id || variant.colorId));

  return (
    <div style={{
      border: "2px solid #e5e7eb", borderRadius: "14px",
      overflow: "hidden", background: "#fff",
    }}>
      {/* Color header */}
      <div style={{
        padding: "12px 18px", background: "#f8fafc",
        borderBottom: "1.5px solid #e5e7eb",
        display: "flex", alignItems: "center", gap: "10px",
      }}>
        {colorObj?.code && (
          <span style={{
            width: "18px", height: "18px", borderRadius: "50%",
            backgroundColor: colorObj.code, border: "2px solid #d1d5db", flexShrink: 0,
          }} />
        )}
        <span style={{ fontWeight: 700, fontSize: "14px", color: "#1e293b" }}>
          {colorObj?.name || variant.colorId?.name || "Color"}
        </span>
      </div>

      {/* Orientation tabs */}
      <div style={{ display: "flex", borderBottom: "1.5px solid #e5e7eb" }}>
        {ORIENTATIONS.map((ori) => {
          const isActive = activeOri === ori;
          const oriData  = variant[ori];
          const hasImgs  = oriData?.images?.length > 0;
          const hasPrev  = !!oriData?.previewImage;
          const complete = hasImgs && hasPrev;
          const icon     = ori === "landscape" ? "🖼️" : "📱";
          return (
            <button
              key={ori}
              type="button"
              onClick={() => setActiveOri(ori)}
              style={{
                flex: 1, padding: "10px", border: "none",
                borderBottom: isActive ? "3px solid #0d6efd" : "3px solid transparent",
                background: isActive ? "#f0f5ff" : "#fff",
                color: isActive ? "#0d6efd" : "#6b7280",
                fontWeight: isActive ? 700 : 500, fontSize: "13px",
                cursor: "pointer", display: "flex", alignItems: "center",
                justifyContent: "center", gap: "6px", transition: "all 0.15s",
              }}
            >
              {icon} {ori.charAt(0).toUpperCase() + ori.slice(1)}
              {complete && (
                <span style={{
                  fontSize: "10px", background: "#10b981", color: "#fff",
                  borderRadius: "10px", padding: "1px 6px",
                }}>✓</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Orientation content */}
      <div style={{ padding: "20px" }}>
        {ORIENTATIONS.map((ori) => (
          <div key={ori} style={{ display: ori === activeOri ? "block" : "none" }}>
            <OrientationView oriData={variant[ori]} orientation={ori} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   ViewProduct
──────────────────────────────────────────────────────────────────── */
export default function ViewProduct() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id }   = useParams();

  const { singleProduct, loading } = useSelector((state) => state.product);

  useEffect(() => {
    dispatch(fetchProductById(id));
  }, [dispatch, id]);

  if (loading) {
    return (
      <div className="row mt-3">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-body text-center py-5">
              <div className="spinner-border text-primary" role="status" />
              <p className="mt-3 text-muted">Loading product details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!singleProduct) {
    return (
      <div className="row mt-3">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="bx bx-error-circle display-1 text-danger" />
              <p className="mt-3">Product not found</p>
              <button className="btn btn-primary" onClick={() => navigate("/admin/product/list")}>
                Back to List
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { colorVariants = [], colors = [], sizes = [] } = singleProduct;

  return (
    <div className="container-fluid mt-3">

      {/* ── Top bar ── */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate("/admin/product/list")}>
          <i className="bx bx-arrow-back me-1" />Back to Products
        </button>
        <button className="btn cusBTNPop btn-sm" onClick={() => navigate(`/admin/products/edit/${id}`)}>
          <i className="bx bx-edit-alt me-1" />Edit Product
        </button>
      </div>

      <div className="row g-3">

        {/* ══════════════════════════════════
            LEFT COLUMN — Product info
        ══════════════════════════════════ */}
        <div className="col-lg-4">

          {/* Basic info card */}
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-light d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">Product Info</h5>
              <span className={`badge ${singleProduct.status ? "bg-success" : "bg-danger"}`}>
                {singleProduct.status ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: "14px" }}>

              <InfoRow icon="bx-box"      label="Name"         value={singleProduct.name} />
              <InfoRow icon="bx-category" label="Category"     value={singleProduct.category?.name} />
              <InfoRow icon="bx-sitemap"  label="Sub-Category" value={singleProduct.subCategory?.name} />
              <InfoRow icon="bx-file"     label="Description"  value={singleProduct.description} muted />

              <div style={{ display: "flex", gap: "10px" }}>
                <div style={{
                  flex: 1, background: "#f0f5ff", borderRadius: "8px",
                  padding: "10px", textAlign: "center",
                }}>
                  <div style={{ fontSize: "11px", color: "#6b7280" }}>Text Zones</div>
                  <div style={{ fontWeight: 700, fontSize: "20px", color: "#0d6efd" }}>
                    {singleProduct.textCount || 0}
                  </div>
                </div>
                <div style={{
                  flex: 1, background: "#f0fdf4", borderRadius: "8px",
                  padding: "10px", textAlign: "center",
                }}>
                  <div style={{ fontSize: "11px", color: "#6b7280" }}>Image Zones</div>
                  <div style={{ fontWeight: 700, fontSize: "20px", color: "#10b981" }}>
                    {singleProduct.imageCount || 0}
                  </div>
                </div>
              </div>

              {singleProduct.bestSellingProduct && (
                <div style={{
                  background: "#fffbeb", border: "1.5px solid #F59E0B",
                  borderRadius: "8px", padding: "8px 12px",
                  fontSize: "13px", color: "#d97706", fontWeight: 600,
                  display: "flex", alignItems: "center", gap: "6px",
                }}>
                  ⭐ Best Selling Product
                </div>
              )}
            </div>
          </div>

          {/* Label */}
          {singleProduct.label && (
            <div className="card mt-3 border-0 shadow-sm">
              <div className="card-header bg-light">
                <h6 className="card-title mb-0">Product Label</h6>
              </div>
              <div className="card-body">
                <span className="badge bg-primary fs-6 px-3 py-2">
                  <i className="bx bx-purchase-tag me-1" />
                  {singleProduct.label.name}
                </span>
              </div>
            </div>
          )}

          {/* Colors */}
          <div className="card mt-3 border-0 shadow-sm">
            <div className="card-header bg-light">
              <h6 className="card-title mb-0">
                <i className="bx bx-palette me-1 text-primary" />Color Options
              </h6>
            </div>
            <div className="card-body">
              <div className="d-flex flex-wrap gap-2">
                {colors.length > 0 ? colors.map((color) => (
                  <div key={color._id}
                    className="d-flex align-items-center gap-2 px-3 py-2 border rounded"
                    style={{ fontSize: "13px" }}>
                    <span className="rounded-circle" style={{
                      width: "18px", height: "18px",
                      backgroundColor: color.code, border: "2px solid #dee2e6",
                    }} />
                    {color.name}
                  </div>
                )) : <span className="text-muted">No colors</span>}
              </div>
            </div>
          </div>

          {/* Size & Pricing */}
          <div className="card mt-3 border-0 shadow-sm">
            <div className="card-header bg-light">
              <h6 className="card-title mb-0">
                <i className="bx bx-ruler me-1 text-primary" />Size &amp; Pricing
              </h6>
            </div>
            <div className="card-body p-0">
              {sizes.length > 0 ? (
                <table className="table table-hover mb-0" style={{ fontSize: "13px" }}>
                  <thead className="table-light">
                    <tr>
                      <th style={{ paddingLeft: "16px" }}>Size</th>
                      <th>MRP</th>
                      <th>Selling</th>
                      <th>Off</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sizes.map((s, i) => {
                      const mrp      = s.mrp || 0;
                      const sp       = s.sellingPrice || 0;
                      const discount = mrp > sp ? Math.round(((mrp - sp) / mrp) * 100) : 0;
                      return (
                        <tr key={i}>
                          <td style={{ paddingLeft: "16px" }}>
                            <span className="badge bg-primary bg-opacity-10 text-primary px-2 py-1">
                              📐 {s.size?.name || "—"}
                            </span>
                          </td>
                          <td><span className="text-muted text-decoration-line-through">₹{mrp}</span></td>
                          <td><span className="fw-bold text-success">₹{sp}</span></td>
                          <td>
                            {discount > 0
                              ? <span className="badge bg-success">{discount}%</span>
                              : <span className="text-muted">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="p-3 text-muted" style={{ fontSize: "13px" }}>No size data</div>
              )}
            </div>
          </div>

        </div>

        {/* ══════════════════════════════════
            RIGHT COLUMN — Color variants
        ══════════════════════════════════ */}
        <div className="col-lg-8">
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

            {colorVariants.length > 0 ? (
              colorVariants.map((variant, i) => (
                <ColorVariantCard key={i} variant={variant} colors={colors} />
              ))
            ) : (
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5 text-muted">
                  <span style={{ fontSize: "40px" }}>🎨</span>
                  <p className="mt-2">No color variants found</p>
                </div>
              </div>
            )}

          </div>
        </div>

      </div>
    </div>
  );
}

/* ── Small helper for info rows ── */
function InfoRow({ icon, label, value, muted }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
      <i className={`bx ${icon} text-primary fs-5`} style={{ marginTop: "2px", flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: "11px", color: "#9ca3af" }}>{label}</div>
        <div style={{
          fontWeight: muted ? 400 : 600,
          color: muted ? "#6b7280" : "#1e293b",
          fontSize: "13px",
        }}>
          {value || "N/A"}
        </div>
      </div>
    </div>
  );
}